import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useProfile } from "@/hooks/useProfile";
import { useTheme, type ThemeOption } from "@/hooks/useTheme";
import { IS_CLICK_DOMAIN } from "@/lib/env";
import { getBotPhotoUrl } from "@/api/client";
import { LanguageList, loadLocaleMessages } from "@/app/i18n";
import { LoadingScreen } from "@/components/LoadingScreen";
import {
  Card,
  ListRow,
  SegmentedTabs,
  BottomSheet,
  Icon,
  ICONS,
} from "@/components/kit";

type ProfileView = "main" | "create-app" | "my-apps";

interface ProfilePageProps {
  onNavigate: (view: ProfileView) => void;
}

/** Foydalanuvchi bosh harflarini qaytaradi */
function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}

/** Til kaliti → bayroq ikonasi */
const LANG_FLAG: Record<string, string> = {
  uz_latn: ICONS.flagUz,
  ru: ICONS.flagRu,
  en: ICONS.flagEn,
};

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="px-1 pb-2 pt-5 text-base font-bold" style={{ color: "var(--c-text)" }}>
      {label}
    </p>
  );
}

export function ProfilePage({ onNavigate }: ProfilePageProps) {
  const { t } = useTranslation();
  const { profile, isLoading } = useProfile();
  const { theme, setTheme } = useTheme();
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState<string>(
    () => localStorage.getItem("Lang") || "uz_latn",
  );

  const photoUrl = profile ? getBotPhotoUrl(profile.photoId) : null;
  const currentLang = LanguageList.find((l) => l.key === lang);

  const themeItems: { id: ThemeOption; label: string }[] = [
    { id: "auto", label: t("themeAuto") },
    { id: "light", label: t("themeLight") },
    { id: "dark", label: t("themeDark") },
  ];

  const handleChangeLang = async (key: string) => {
    localStorage.setItem("Lang", key);
    await loadLocaleMessages(key);
    setLang(key);
    setLangOpen(false);
  };

  return (
    <div className="pt-5 pb-4">
      {/* Header */}
      <div className="mb-4 flex items-end justify-between gap-2">
        <h1 className="text-2xl font-bold" style={{ color: "var(--c-text)" }}>
          {t("navProfile")}
        </h1>
        {profile?.createdAt && (
          <span className="text-xs" style={{ color: "var(--c-text-muted)" }}>
            {t("profileJoinedAt")}: {profile.createdAt}
          </span>
        )}
      </div>

      {/* Profile card */}
      {isLoading ? (
        <LoadingScreen className="min-h-[88px]" />
      ) : (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <Card className="flex items-center gap-3 p-3">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={profile?.fullName}
                className="size-12 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div
                className="grid size-12 shrink-0 place-items-center rounded-full text-base font-bold"
                style={{ background: "var(--primary)", color: "var(--c-on-brand)" }}
              >
                {profile ? getInitials(profile.fullName) : "?"}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-base font-semibold" style={{ color: "var(--c-text)" }}>
                {profile?.fullName ?? "—"}
              </p>
              <p className="truncate text-sm" style={{ color: "var(--c-text-muted)" }}>
                {profile?.phoneNumber ?? "—"}
              </p>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Arizalar */}
      <SectionLabel label={t("profileActivity")} />
      <Card className="overflow-hidden [&>*+*]:border-t [&>*+*]:border-[var(--c-line)]">
        <ListRow
          icon={ICONS.createApp}
          title={t("profileCreateApp")}
          subtitle={t("profileCreateAppDesc")}
          onClick={() => onNavigate("create-app")}
        />
        <ListRow
          icon={ICONS.myApps}
          title={t("profileMyApps")}
          subtitle={t("profileMyAppsDesc")}
          onClick={() => onNavigate("my-apps")}
        />
      </Card>

      {/* Sozlamalar — Click domainida super app boshqaradi */}
      {!IS_CLICK_DOMAIN && (
        <>
          <SectionLabel label={t("profileSettings")} />
          <Card className="overflow-hidden [&>*+*]:border-t [&>*+*]:border-[var(--c-line)]">
            <ListRow
              icon={ICONS.language}
              title={t("profileLanguage")}
              subtitle={t("profileLangDesc")}
              trailing={
                <span className="flex items-center gap-1.5" style={{ color: "var(--c-text-muted)" }}>
                  <span className="text-sm font-medium">{currentLang?.fullName}</span>
                  <Icon name={ICONS.chevronRight} size={18} />
                </span>
              }
              onClick={() => setLangOpen(true)}
            />

            {/* Mavzu */}
            <div className="px-4 py-3.5">
              <div className="mb-2.5 flex items-center gap-3">
                <span
                  className="grid size-9 shrink-0 place-items-center rounded-xl"
                  style={{ background: "var(--c-brand-tint)", color: "var(--primary)" }}
                >
                  <Icon name={ICONS.theme} size={20} />
                </span>
                <span className="text-base font-semibold" style={{ color: "var(--c-text)" }}>
                  {t("theme")}
                </span>
              </div>
              <SegmentedTabs<ThemeOption> items={themeItems} value={theme} onChange={setTheme} />
            </div>
          </Card>
        </>
      )}

      {/* Til tanlash sheet */}
      <BottomSheet open={langOpen} onOpenChange={setLangOpen} title={t("selectLanguage")} align="center">
        <div className="flex flex-col gap-2 pb-2">
          {LanguageList.map((item) => {
            const active = lang === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleChangeLang(item.key)}
                className="flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors"
                style={{ background: active ? "var(--c-brand-tint)" : "var(--c-elevated-2)" }}
              >
                <Icon name={LANG_FLAG[item.key] ?? ICONS.language} size={28} />
                <span className="flex-1 text-left text-base font-semibold" style={{ color: "var(--c-text)" }}>
                  {item.fullName}
                </span>
                <span
                  className="grid size-5 place-items-center rounded-full"
                  style={{
                    boxShadow: active ? "none" : "inset 0 0 0 2px var(--c-text-disabled)",
                    background: active ? "var(--primary)" : "transparent",
                    color: "var(--c-on-brand)",
                  }}
                  aria-hidden
                >
                  {active && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
}
